import { useEffect, useState } from 'react';

// Measure the element behind `ref` and derive how many square-ish cards fill
// it: columns are as many ≥minCardWidth-wide cards as fit across (capped at
// maxCols), rows as many ≥minRowHeight-tall rows as fit, and never more cells
// than one fetch (maxItems) can fill. Re-measures on resize and when any of
// `deps` change. Returns the { cols, rows } arrangement (null until measured)
// and its pageSize.
export function useFitGrid(ref, {
  deps = [],
  minCardWidth = 230,
  minRowHeight = 200,
  maxCols = 4,
  chrome = 56,
  gap = 16,
  maxItems = 12,
} = {}) {
  const [arrangement, setArrangement] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight - chrome;
      if (!width || height <= 0) return;
      const cols = Math.max(1, Math.min(maxCols, Math.floor(width / minCardWidth)));
      // Prefer near-square cells (cards then fill their columns edge to edge),
      // but never let rows get shorter than the minimum card height, nor lay
      // out more cells than one fetch can fill.
      const colWidth = (width - (cols - 1) * gap) / cols;
      const rows = Math.max(1, Math.min(
        Math.round(height / colWidth),
        Math.floor(height / minRowHeight),
        Math.floor(maxItems / cols),
      ));
      setArrangement((prev) => (
        prev && prev.cols === cols && prev.rows === rows ? prev : { cols, rows }
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, deps);

  const pageSize = arrangement ? arrangement.cols * arrangement.rows : 0;
  return { arrangement, pageSize };
}

export default useFitGrid;
